import PluginHeader from "../PluginHeader";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useParams } from "react-router-dom";

const FijiVmsPlugin = () => {
  const { lang } = useParams();

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Fiji VMS E-invoicing - Aronium</title>
          <meta name="description" content="Prepare Aronium for Fiji VAT Monitoring System requirements using the Fiji VMS plugin" />
        </Helmet>
      </HelmetProvider>

      <section className="py-8">
        <div className="max-w-screen-xl mx-auto text-center px-4">
          <h1 className="text-4xl mb-4">
            VMS E-Invoicing in Fiji
          </h1>
          <p>
            Connect Aronium to Fiji's VAT Monitoring System and issue compliant fiscal invoices directly from your POS.
          </p>
          <p className="mt-1 text-sm">
            Built for businesses preparing to use FRCS's V-SDC or connect Aronium with external E-SDC components.
          </p>

          <div className="mx-auto mt-8 max-w-3xl rounded-lg bg-[#1d4675] px-6 py-5 text-white shadow-sm">
            <h2 className="text-2xl font-semibold">Private BETA</h2>
            <p className="mt-2">
              Fiji VMS plugin is currently available for early adopters.<br />If you are interested in testing it or need VMS support for your business, contact us at{" "}
              <a href="mailto:support@aronium.com" className="font-semibold text-white underline decoration-white/70 underline-offset-4 hover:text-sky-100">
                support@aronium.com
              </a>.
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto mb-10 flex items-center justify-center px-4">
        <div className="rounded-lg bg-[#1d4675] px-8 py-7 shadow-sm">
          <img className="h-20 sm:h-24 max-w-full object-contain" src={`${process.env.PUBLIC_URL}/images/fiji.svg`} alt="Fiji Revenue and Customs Service" />
        </div>
      </div>

      <div className="py-20 bg-[#1d4675]">
        <PluginHeader price="10"
          note={[
            "Supports V-SDC and E-SDC integrations."
          ]} />
      </div>

      <div className="pt-24 pb-12">
        <div className="relative max-w-7xl mx-auto text-center px-8 lg:px-12">
          <h2 className="text-5xl text-gray-800 leading-tight mb-8">
            Why use the <span className="font-semibold">Fiji VMS</span> plugin?
          </h2>
          <p className="text-lg text-gray-600 mx-auto">
            Aronium's <span className="font-semibold">Fiji VMS</span> plugin is designed to connect everyday POS operations with Fiji's fiscalization process. It helps sales, refunds, and invoices move through the required Sales Data Controller workflow so your business can issue compliant fiscal documents from Aronium.
          </p>
        </div>
      </div>

      <div className="mb-20 px-4">
        <div className="mx-auto max-w-3xl rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm">
          <p className="text-sm font-semibold uppercase tracking-wide text-sky-700">Official framework</p>
          <h3 className="mt-3 text-3xl font-semibold text-gray-800">Fiji VAT Monitoring System</h3>
          <p className="mt-4 text-gray-600">
            Fiji's VMS requires transactions to be recorded through an Electronic Fiscal Device made up of a POS, a Sales Data Controller, and a Secure Element. The SDC fiscalizes the transaction, returns the fiscal invoice data to the POS, and transmits fiscal data to FRCS.
          </p>
          <a
            href="https://frcs.org.fj/vms/"
            className="button-rounded-white mt-8"
            target="_blank"
            rel="noreferrer"
          >
            Official Fiji VMS information
          </a>
        </div>
      </div>

      <div className="pb-24">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white p-8 shadow-lg rounded-lg border">
            <h3 className="text-2xl font-semibold text-gray-800">Fiji fiscalization workflow</h3>
            <p className="mt-4 text-gray-600">
              Aronium is being prepared for Fiji's VMS flow, where the POS communicates with an SDC before the fiscal invoice is issued.
            </p>
          </div>
          <div className="bg-white p-8 shadow-lg rounded-lg border">
            <h3 className="text-2xl font-semibold text-gray-800">V-SDC and E-SDC integration</h3>
            <p className="mt-4 text-gray-600">
              The plugin is designed to work with the FRCS Virtual Sales Data Controller and with supported third-party External Sales Data Controller setups.
            </p>
          </div>
          <div className="bg-white p-8 shadow-lg rounded-lg border">
            <h3 className="text-2xl font-semibold text-gray-800">Fiscal invoice handling</h3>
            <p className="mt-4 text-gray-600">
              Transaction data is sent for fiscalization, then the signed fiscal response is used by Aronium so customers receive the required invoice or receipt details.
            </p>
          </div>
          <div className="bg-white p-8 shadow-lg rounded-lg border">
            <h3 className="text-2xl font-semibold text-gray-800">Guided BETA rollout</h3>
            <p className="mt-4 text-gray-600">
              During private BETA, we work directly with interested businesses to review their requirements, selected SDC configuration, and the steps needed before production use.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-tr from-[#23263d] to-sky-600 py-24 text-center text-white">
        <div className="relative mx-auto px-4">
          <h2 className="text-3xl md:text-6xl mb-6">
            Ready for Fiji VMS?
          </h2>
          <p className="text-xl mb-10">
            Start using the <span className="font-semibold">Fiji VMS</span> plugin to prepare Aronium for Fiji electronic fiscal reporting.
          </p>
          <a
            href={`/${lang}/contact`}
            className="button-rounded-white px-12 py-4"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="ml-2 w-6 h-6 transition-all mr-2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
            </svg> Contact us
          </a>
        </div>
      </div>
    </>
  );
};

export default FijiVmsPlugin;
